import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * Pictures for a scene's panel or a band's cell, from THIS episode: the
 * collection's ready pictures first, then the show's logos — never the whole
 * library, which is the point of the Idea phase. Thumbs are the cached small
 * copies. `value` is the chosen ids in order; `max` caps them.
 */
export function PicturePicker({ cardId, logos, value, max, onChange, tool, refreshKey }: {
  cardId?: string;
  /** Changes when the collection does, so a picture gathered a moment ago is offered. */
  refreshKey?: string;
  /** The show's logos (097), offered after the collection. */
  logos?: string[] | null;
  value: string[];
  max: number;
  onChange: (ids: string[]) => void;
  tool?: string;
}) {
  const [choices, setChoices] = useState<Array<{ id: string; name: string }>>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    const load = async () => {
      const pics: Array<{ id: string; name: string }> = [];
      if (cardId) {
        try {
          const items = await api.collection(cardId);
          for (const i of items) {
            if (i.asset_id && i.asset_kind === 'image' && i.asset_status === 'ready') {
              pics.push({ id: i.asset_id, name: i.asset_name ?? i.storage_key?.split('/').pop() ?? 'picture' });
            }
          }
        } catch { /* a collection that will not load offers the logos alone */ }
      }
      for (const id of logos ?? []) if (!pics.some((p) => p.id === id)) pics.push({ id, name: 'logo' });
      if (!live) return;
      setChoices(pics);
      for (const pic of pics) {
        api.assetThumbnail(pic.id).then((u) => { if (live && u) setThumbs((t) => ({ ...t, [pic.id]: u })); }).catch(() => undefined);
      }
    };
    void load();
    return () => { live = false; };
  }, [cardId, (logos ?? []).join(','), refreshKey]);
  const toggle = (id: string) => {
    const has = value.includes(id);
    if (!has && value.length >= max) {
      if (max === 1) { onChange([id]); return; }
      return;
    }
    onChange(has ? value.filter((p) => p !== id) : [...value, id]);
  };
  if (choices.length === 0) {
    return <span className="hint">No pictures in this episode yet — gather some, or add from the library, in the collection above.</span>;
  }
  return (
    <div className="insp-pics" data-tool={tool ?? 'pictures'}>
      {choices.map((c) => {
        const on = value.includes(c.id);
        return (
          <button key={c.id} type="button" className={`insp-pic${on ? ' is-on' : ''}`} title={c.name}
                  aria-pressed={on} onClick={() => toggle(c.id)}
                  disabled={!on && max > 1 && value.length >= max}>
            {thumbs[c.id] ? <img src={thumbs[c.id]} alt="" /> : <span className="insp-pic-blank" />}
            {on && max > 1 && <span className="insp-pic-n">{value.indexOf(c.id) + 1}</span>}
          </button>
        );
      })}
    </div>
  );
}
